/* AssociateTasks — Sandboxed task list for the Data Entry Associate.
 * Text-only data, no attachments, no client identity leaks, no office brand. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, Briefcase, AlertCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_AR = { riyadh: "الرياض", jeddah: "جدة", dammam: "الدمام" };

export default function AssociateTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "لوحة المساند";
    (async () => {
      try {
        const token = localStorage.getItem("vta_token");
        const { data } = await axios.get(`${API}/associate/tasks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(data.tasks || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "تعذّر تحميل المهام");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div style={s.wrap} data-testid="associate-tasks-page">
      <div style={s.container}>
        <header style={s.header}>
          <h1 style={s.h1}>لوحة مهام المساند</h1>
          <p style={s.subtitle}>إدخال البيانات النصية فقط — بدون مرفقات، بدون فواتير، بدون تواصل مع العملاء.</p>
          <div style={s.toolbar}>
            <Link to="/associate/training" style={s.trainingBtn} data-testid="open-training-btn">
              ابدأ تدريباً عملياً
            </Link>
            <Link to="/associate/assessment" style={s.assessmentBtn} data-testid="open-assessment-btn">
              ابدأ التقييم النهائي
            </Link>
          </div>
        </header>

        {loading && <div style={s.loading}>جارٍ التحميل…</div>}
        {err && <div style={s.error} data-testid="tasks-error">{err}</div>}

        {!loading && !tasks.length && (
          <div style={s.empty} data-testid="tasks-empty">
            <AlertCircle size={28} color="#d4af37" />
            <div style={{ marginTop: 8 }}>لا توجد مهام مُسندة لك حالياً.</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              استخدم التدريب العملي لتجهيز نفسك حتى تصلك المهام.
            </div>
          </div>
        )}

        <div style={s.grid}>
          {tasks.map((t) => (
            <Link to={`/associate/tasks/${t.id}`} key={t.id} style={s.card} data-testid={`task-card-${t.id}`}>
              <div style={s.cardHead}>
                <span style={s.taskNum}>{t.task_number}</span>
                <span style={s.statusPill}>{t.status_code}</span>
              </div>
              <div style={s.country}>{t.country} — {t.visa_type || "—"}</div>
              <div style={s.metaRow}>
                <span style={s.meta}><MapPin size={13} /> {CITY_AR[t.biometric_city] || t.biometric_city || "—"}</span>
                <span style={s.meta}><Users size={13} /> {t.members_count}</span>
                <span style={s.meta}><Briefcase size={13} /> {t.applicant_type}</span>
              </div>
              <div style={s.dates}>
                <Calendar size={12} /> سفر: {t.travel_date || "—"}  · عودة: {t.return_date || "—"}
              </div>
              <div style={s.client}>المتقدم: {t.client_initials}</div>
              {t.appointment_chosen && (
                <div style={s.appt}>
                  <Clock size={12} /> موعد مختار: {new Date(t.appointment_chosen.datetime).toLocaleString("en-US")}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const s = {
  wrap: { minHeight: "100vh", background: "#0a0a0f", color: "#e9e9ec", padding: "32px 16px",
          fontFamily: "'Tajawal', system-ui, sans-serif", direction: "rtl" },
  container: { maxWidth: 1120, margin: "0 auto" },
  header: { marginBottom: 28 },
  h1: { fontSize: 28, fontWeight: 900, margin: 0, color: "#d4af37" },
  subtitle: { fontSize: 13, color: "#9a9a9f", marginTop: 6, lineHeight: 1.7 },
  toolbar: { marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" },
  trainingBtn: { padding: "10px 18px", background: "#1a1a25", color: "#d4af37", border: "1px solid #2a2a3a",
                 borderRadius: 10, textDecoration: "none", fontWeight: 700, fontSize: 13 },
  assessmentBtn: { padding: "10px 18px", background: "#d4af37", color: "#0a0a0f", border: "none",
                   borderRadius: 10, textDecoration: "none", fontWeight: 800, fontSize: 13 },
  loading: { padding: 30, textAlign: "center", color: "#888" },
  error: { padding: 14, background: "#3a1414", border: "1px solid #6b1f1f", borderRadius: 10,
           color: "#fca5a5", marginBottom: 14 },
  empty: { padding: 40, textAlign: "center", background: "#13131a", border: "1px dashed #2a2a3a",
           borderRadius: 14, color: "#bbb" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 },
  card: { background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 14, padding: 16,
          textDecoration: "none", color: "#e9e9ec", transition: "border-color .15s",
          display: "flex", flexDirection: "column", gap: 8 },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  taskNum: { fontSize: 13, fontWeight: 800, color: "#d4af37", letterSpacing: 0.3 },
  statusPill: { fontSize: 10, padding: "3px 8px", background: "#1f2937", border: "1px solid #374151",
                borderRadius: 999, color: "#d1d5db" },
  country: { fontSize: 15, fontWeight: 700, color: "#fff" },
  metaRow: { display: "flex", gap: 10, fontSize: 11, color: "#a3a3a8", flexWrap: "wrap" },
  meta: { display: "inline-flex", alignItems: "center", gap: 4 },
  dates: { fontSize: 11, color: "#888", display: "inline-flex", alignItems: "center", gap: 4 },
  client: { fontSize: 12, color: "#cbb775", marginTop: 4 },
  appt: { fontSize: 11, color: "#34d399", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4 },
};
